import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Column } from "primereact/column";

import CursorDataTable from "../../../components/CursorDataTable";
import { useCursorPagination } from "../../../hooks/useCursorPagination";
import { listDispatches } from "../../../services/notificationDispatches";
import { DispatchRecord } from "../../../types/alerting";
import { outcomeOf } from "../../../utils/dispatchOutcome";
import { formatDuration } from "../../../utils/formatDuration";
import { formatInstant, secondsBetween } from "../../../utils/instantFormat";
import OutcomeTag from "./OutcomeTag";
import { buildDayShades, dayKey, startsNewDay } from "./dayShading";
import { getCochargeUserByEmaid } from "../../../services/owners";
import "./dayShading.css";

const PAGE_SIZE = 50;
const NOTEWORTHY_LAG_SECONDS = 60;

/** Reads the station from the route so a support agent can be linked straight to it. */
const NotificationHistoryPage = () => {
  const { locationId = "" } = useParams();
  const navigate = useNavigate();

  const fetchPage = useCallback(
    (cursor: string | null) => listDispatches(locationId, { limit: PAGE_SIZE, cursor }),
    [locationId],
  );

  const { rows, state, hasMore, loadingMore, error, loadMore } =
    useCursorPagination<DispatchRecord>(fetchPage);

  const [emails, setEmails] = useState<Record<string, string | null>>({});

  /** One lookup per distinct recipient, so the audit names a person and not just an identifier. */
  useEffect(() => {
    const unresolved = Array.from(new Set(rows.map((r) => r.recipient))).filter(
      (id) => id && !(id in emails),
    );
    if (unresolved.length === 0) return;

    let cancelled = false;
    void Promise.all(
      unresolved.map(async (id) => {
        const user = await getCochargeUserByEmaid(id).catch(() => null);
        return [id, user?.email ?? null] as const;
      }),
    ).then((pairs) => {
      if (!cancelled) setEmails((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });

    return () => {
      cancelled = true;
    };
  }, [rows, emails]);

  const shades = useMemo(() => buildDayShades(rows), [rows]);

  const rowClassName = (row: DispatchRecord) => {
    const shade = shades.get(dayKey(row.dispatchedAt)) ?? 0;
    const index = rows.indexOf(row);
    return `dispatch-day-${shade}${startsNewDay(rows, index) ? " dispatch-day-start" : ""}`;
  };

  const recipientBody = (record: DispatchRecord) => (
    <div className="flex flex-column">
      <span>{record.recipient}</span>
      {emails[record.recipient] && (
        <span className="text-xs text-color-secondary">{emails[record.recipient]}</span>
      )}
    </div>
  );

  const recordedBody = (record: DispatchRecord) => {
    const lag = secondsBetween(record.dispatchedAt, record.recordedAt);
    const noteworthy = lag !== null && Math.abs(lag) >= NOTEWORTHY_LAG_SECONDS;

    return (
      <div className="flex flex-column gap-1">
        <span>{formatInstant(record.recordedAt)}</span>
        {noteworthy && (
          <span className="text-xs text-orange-600" data-testid="recording-lag">
            recorded {formatDuration(Math.abs(lag as number))} after sending
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="surface-section px-4 py-5 md:px-6 lg:px-8">
      <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3 mb-4">
        <div>
          <div className="text-2xl font-semibold">Notification history</div>
          <div className="text-color-secondary text-sm mt-1">{locationId}</div>
        </div>
        <Button
          label="Back to station"
          icon="pi pi-arrow-left"
          outlined
          onClick={() => navigate(`/station-details/${locationId}`)}
        />
      </div>

      <p className="text-color-secondary mt-0 mb-4">
        Every message the platform recorded sending about this station, newest first — including the
        ones it deliberately did not send.
      </p>

      <CursorDataTable<DispatchRecord>
        state={state}
        rows={rows}
        dataKey="dispatchId"
        hasMore={hasMore}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
        error={error}
        emptyMessage={`Nothing has been sent about ${locationId}.`}
        refusedMessage={`You are not entitled to see the notification history for ${locationId}.`}
        failedMessage="Could not load the notification history"
        rowClassName={rowClassName}
      >
        <Column header="Recipient" body={recipientBody} />
        <Column field="channel" header="Channel" />
        <Column field="notificationType" header="Type" />
        <Column header="Sent" body={(r: DispatchRecord) => formatInstant(r.dispatchedAt)} />
        <Column header="Recorded" body={recordedBody} />
        <Column
          header="Outcome"
          body={(r: DispatchRecord) => (
            <OutcomeTag outcome={outcomeOf(r)} reason={r.suppressionReason} />
          )}
        />
      </CursorDataTable>
    </div>
  );
};

export default NotificationHistoryPage;
